export const hasValue = (value) => value !== null && value !== undefined && value !== '';

export const formatCurrency = (value, suffix = '') => {
  if (!hasValue(value)) return '—';
  return `€${Number(value).toLocaleString()}${suffix}`;
};

export const resolveLocaleTag = (locale = 'en') => (locale === 'de' ? 'de-DE' : 'en-US');

export const formatDate = (value, fallback = null, locale = 'en') => {
  if (!value) return fallback ?? null;
  try {
    return new Date(value).toLocaleDateString(resolveLocaleTag(locale), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return fallback ?? null;
  }
};

export const formatMonthYear = (date, locale = 'en') =>
  date.toLocaleDateString(resolveLocaleTag(locale), { month: 'long', year: 'numeric' });

export const getWeekdayLabels = (locale = 'en') =>
  Array.from({ length: 7 }, (_, index) => {
    const date = new Date(2024, 0, 7 + index);
    return date.toLocaleDateString(resolveLocaleTag(locale), { weekday: 'narrow' });
  });

export const getBaseRent = (listing) => listing?.base_rent ?? listing?.base_amount ?? null;

export const getMonthlyTotal = (listing) =>
  listing?.total_monthly_cost ?? listing?.total_monthly ?? listing?.warmmiete ?? null;

export const getMonthlyReferenceAmount = (listing) => getMonthlyTotal(listing) ?? getBaseRent(listing);

export const getMonthlyPriceLabel = (listing) =>
  hasValue(getMonthlyTotal(listing)) ? 'known monthly total' : 'base rent';

export const getMonthlyPriceLabelKey = (listing) =>
  hasValue(getMonthlyTotal(listing)) ? 'property.confirmed_monthly_total' : 'property.base_rent_only';

const OWNER_CONFIRMATION_RECENT_MS = 30 * 24 * 60 * 60 * 1000;

export const parseOwnerConfirmationDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isOwnerConfirmationRecent = (lastConfirmedAt, now = new Date()) => {
  const parsed = parseOwnerConfirmationDate(lastConfirmedAt);
  if (!parsed) return false;
  const ageMs = now.getTime() - parsed.getTime();
  return ageMs >= 0 && ageMs <= OWNER_CONFIRMATION_RECENT_MS;
};

export const getOwnerConfirmationLabel = (listing, t) => {
  const lastConfirmedAt = listing?.last_confirmed_at;
  if (isOwnerConfirmationRecent(lastConfirmedAt)) {
    const dateLabel = formatDate(lastConfirmedAt, '');
    return translateTemplate(
      t('property.owner_updated_date', 'Owner-updated {date}'),
      { date: dateLabel },
    );
  }
  return t('property.owner_not_recently_confirmed', 'Not recently confirmed');
};

export const translateTemplate = (template, replacements = {}) =>
  Object.entries(replacements).reduce(
    (result, [token, value]) => result.replaceAll(`{${token}}`, String(value ?? '')),
    template,
  );
